
import { useState, useEffect } from "react";
import { Users, DollarSign, AlertCircle, TrendingUp, Search } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import { reportsApi } from "@/lib/api";
import { formatCurrencyFull, localToday } from "@/lib/data";

const fmt = (n: number) => formatCurrencyFull(n);

function KpiCard({ label, value, sub, icon: Icon, color, bg }: any) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-stone-100`}>
      <div className={`w-9 h-9 rounded-lg ${color} bg-white/80 flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-medium text-stone-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-stone-800">{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function ClientAnalyticsPage() {
  const today = localToday();
  const firstOfYear = today.slice(0, 4) + "-01-01";

  const [fromDate, setFromDate] = useState(firstOfYear);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await reportsApi.clientAnalytics({ from_date: fromDate, to_date: toDate });
      setData(res.data?.data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [fromDate, toDate]);

  // Backend returns all clients; filter to those with events in the period
  const allClients: any[] = data?.clients ?? [];
  const clients = allClients.filter((c: any) => Number(c.total_events ?? 0) > 0);
  const summary = data?.summary ?? {};

  const totalClients   = clients.length;
  const totalRevenue   = summary.total_revenue ?? 0;
  const totalDues      = summary.total_outstanding ?? 0;
  const avgPerClient   = totalClients > 0 ? totalRevenue / totalClients : 0;

  const filtered = clients.filter((c: any) =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h1 className="text-lg font-bold text-stone-800">Client Analytics</h1>
          </div>
          <DateRangePicker
            fromDate={fromDate} toDate={toDate}
            onFromChange={setFromDate} onToChange={setToDate}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-stone-400 text-sm">Loading…</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard label="Active Clients" value={totalClients} sub="with events in range" icon={Users} color="text-blue-600" bg="bg-blue-50" />
              <KpiCard label="Total Revenue" value={fmt(totalRevenue)} sub="collected" icon={DollarSign} color="text-emerald-600" bg="bg-emerald-50" />
              <KpiCard label="Outstanding Dues" value={fmt(totalDues)} sub="across all clients" icon={AlertCircle} color="text-red-500" bg="bg-red-50" />
              <KpiCard label="Avg per Client" value={fmt(avgPerClient)} sub="revenue average" icon={TrendingUp} color="text-violet-600" bg="bg-violet-50" />
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-stone-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-stone-700">Client-wise Breakdown</h2>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search client…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-8 pr-3 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                </div>
              </div>
              {filtered.length === 0 ? (
                <p className="text-sm text-stone-400 py-8 text-center">No client data for this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100">
                        <th className="text-left py-2 text-xs font-semibold text-stone-500">#</th>
                        <th className="text-left py-2 text-xs font-semibold text-stone-500">Client</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Events</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Revenue</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Paid</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Outstanding</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Collection %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-50">
                      {filtered.map((c: any, i: number) => {
                        const revenue     = Number(c.total_revenue ?? 0);
                        const paid        = Number(c.total_paid ?? 0);
                        const outstanding = Number(c.outstanding_dues ?? 0);
                        const pct         = revenue > 0 ? (paid / revenue) * 100 : 0;
                        return (
                          <tr key={c.id ?? i} className="hover:bg-stone-50 transition-colors">
                            <td className="py-2.5 text-stone-400 text-xs">{i + 1}</td>
                            <td className="py-2.5">
                              <div className="font-medium text-stone-800">{c.name}</div>
                              {c.phone && <div className="text-xs text-stone-400">{c.phone}</div>}
                            </td>
                            <td className="py-2.5 text-right text-stone-600">{c.total_events ?? 0}</td>
                            <td className="py-2.5 text-right font-semibold text-stone-800">{fmt(revenue)}</td>
                            <td className="py-2.5 text-right text-emerald-600">{fmt(paid)}</td>
                            <td className={`py-2.5 text-right font-medium ${outstanding > 0 ? "text-red-500" : "text-stone-400"}`}>
                              {outstanding > 0 ? fmt(outstanding) : "—"}
                            </td>
                            <td className="py-2.5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-14 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-400" : pct >= 75 ? "bg-blue-400" : pct >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                  />
                                </div>
                                <span className={`text-xs w-9 text-right ${pct >= 100 ? "text-emerald-600" : pct >= 75 ? "text-blue-600" : "text-stone-500"}`}>
                                  {pct.toFixed(0)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-stone-200 bg-stone-50">
                        <td colSpan={2} className="py-2.5 font-bold text-stone-800">Total</td>
                        <td className="py-2.5 text-right font-bold text-stone-700">
                          {filtered.reduce((s: number, c: any) => s + Number(c.total_events ?? 0), 0)}
                        </td>
                        <td className="py-2.5 text-right font-bold text-stone-800">
                          {fmt(filtered.reduce((s: number, c: any) => s + Number(c.total_revenue ?? 0), 0))}
                        </td>
                        <td className="py-2.5 text-right font-bold text-emerald-600">
                          {fmt(filtered.reduce((s: number, c: any) => s + Number(c.total_paid ?? 0), 0))}
                        </td>
                        <td className="py-2.5 text-right font-bold text-red-500">
                          {fmt(filtered.reduce((s: number, c: any) => s + Number(c.outstanding_dues ?? 0), 0))}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
